import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getAvatarColor } from './utils/avatarUtils';
import { getActiveAccount } from './utils/accountUtils';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import {
  MdHome,
  MdOutlineHome,
  MdSubscriptions,
  MdOutlineSubscriptions,
  MdOutlineHistory,
  MdOutlineWatchLater,
  MdOutlineThumbUp,
  MdOutlineVideoLibrary,
  MdOutlineFileDownload,
  MdOutlineShoppingBag,
  MdOutlineMovie,
  MdChevronRight,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdTv,
  MdOutlineTv,
  MdSchool,
  MdOutlineSchool,
  MdWorkspacePremium,
  MdOutlineLibraryMusic,
  MdLibraryMusic,
} from 'react-icons/md';

import {
  RiHistoryFill,
  RiPlayList2Line,
  RiPlayList2Fill,
  RiShoppingBagFill,
  RiFilmFill,
  RiFireLine,
  RiFireFill,
  RiStarLine,
  RiStarFill,
  RiQuestionLine,
  RiQuestionFill,
  RiCalendarEventLine,
  RiCalendarEventFill,
} from 'react-icons/ri';

import {
  BiSolidPlaylist,
  BiSolidLike,
  BiSolidDownload,
} from 'react-icons/bi';

import {
  BsClockFill,
  BsCollectionPlayFill,
} from 'react-icons/bs';

import { SiYoutubekids } from 'react-icons/si';
import { HiOutlineUser, HiUser, HiOutlineUserCircle } from 'react-icons/hi2';
import { getTranslation } from './translations';
import './Sidebar.css';


// Shorts Outline SVG Icon (Initial / Unselected)
const ShortsOutlineIcon = ({ size = 22, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.77 10.32L16.48 9.68L17.79 8.93C19.53 7.93 20.13 5.7 19.13 3.96C18.13 2.22 15.9 1.62 14.16 2.62L6.71 6.89C5.17 7.77 4.19 9.38 4.19 11.16C4.19 12.94 5.17 14.55 6.72 15.43L8.01 16.07L6.7 16.82C4.96 17.82 4.36 20.05 5.36 21.79C6.36 23.53 8.59 24.13 10.33 23.13L17.78 18.86C19.32 17.98 20.3 16.37 20.3 14.59C20.3 12.81 19.32 11.2 17.77 10.32ZM10 14.65V9.35L14.6 12L10 14.65Z"
      stroke={color}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

// Shorts Active SVG Icon (Selected / Filled)
const ShortsActiveIcon = ({ size = 22, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.77 10.32L16.48 9.68L17.79 8.93C19.53 7.93 20.13 5.7 19.13 3.96C18.13 2.22 15.9 1.62 14.16 2.62L6.71 6.89C5.17 7.77 4.19 9.38 4.19 11.16C4.19 12.94 5.17 14.55 6.72 15.43L8.01 16.07L6.7 16.82C4.96 17.82 4.36 20.05 5.36 21.79C6.36 23.53 8.59 24.13 10.33 23.13L17.78 18.86C19.32 17.98 20.3 16.37 20.3 14.59C20.3 12.81 19.32 11.2 17.77 10.32ZM10 14.65V9.35L14.6 12L10 14.65Z" />
  </svg>
);

export default function Sidebar({ collapsed, activePath = '/', onNavigate, isLoggedIn = true, onLogin }) {
  const [showMore, setShowMore] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [subscribedChannels, setSubscribedChannels] = useState([]);
  const [isSubscriptionsOpen, setIsSubscriptionsOpen] = useState(true);
  const [showAllSubs, setShowAllSubs] = useState(false);
  const [, setLangTick] = useState(0);
  const scrollTimeoutRef = useRef(null);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const active = getActiveAccount();
      const token = active?.token || localStorage.getItem('speedotube_user_token');
      if (!token) {
        setSubscribedChannels([]);
        return;
      }
      const headers = {
        token: 'c7e993808a344da9d59cfa20fa8b6061',
        devicetype: '0',
        request_type: 'USER',
        authorization: `Bearer ${token}`,
      };
      const res = await fetch(`${API_BASE_URL}/api/v1/subscribed/channels?limit=50&offset=0`, {
        headers,
      });
      if (res.ok) {
        const json = await res.json();
        let list = json?.data?.channels || [];
        const ownChId = active?.channelId || active?.channel_id;
        const ownHandle = (active?.handle || active?.handleName || '').toLowerCase().replace(/^@/, '');
        const ownName = (active?.name || active?.fullName || '').toLowerCase();
        if (ownChId || ownHandle || ownName) {
          list = list.filter((ch) => {
            if (ownChId && (ch.channel_id === ownChId || String(ch.channel_id) === String(ownChId))) return false;
            const chHandle = (ch.handle_name || '').toLowerCase().replace(/^@/, '');
            const chName = (ch.name || ch.channel_name || '').toLowerCase();
            if (ownHandle && (chHandle === ownHandle || chHandle.includes(ownHandle) || ownHandle.includes(chHandle))) return false;
            if (ownName && (chName === ownName || chName.includes(ownName) || ownName.includes(chName))) return false;
            return true;
          });
        }
        setSubscribedChannels(list);
      }
    } catch (e) {
      console.warn('Failed to fetch subscribed channels:', e);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
    const handleSubChange = () => fetchSubscriptions();
    window.addEventListener('speedotube_subscription_toggled', handleSubChange);
    window.addEventListener('speedotube:subscription-changed', handleSubChange);
    window.addEventListener('speedotube_user_switched', handleSubChange);
    window.addEventListener('focus', handleSubChange);
    window.addEventListener('storage', handleSubChange);
    return () => {
      window.removeEventListener('speedotube_subscription_toggled', handleSubChange);
      window.removeEventListener('speedotube:subscription-changed', handleSubChange);
      window.removeEventListener('speedotube_user_switched', handleSubChange);
      window.removeEventListener('focus', handleSubChange);
      window.removeEventListener('storage', handleSubChange);
    };
  }, [fetchSubscriptions]);

  useEffect(() => {
    const handleLangChange = () => setLangTick((t) => t + 1);
    window.addEventListener('speedotube_language_changed', handleLangChange);
    return () => window.removeEventListener('speedotube_language_changed', handleLangChange);
  }, []);

  const handleSidebarScroll = () => {
    setIsScrolling(true);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 3500); // Fades out completely after 3.5s of inactivity
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const handleNav = (path) => {
    if (onNavigate) onNavigate(path);
  };

  const navItem = (path, labelKey, fallbackLabel, Icon, ActiveIcon, iconColor = null) => {
    const label = getTranslation(labelKey) || fallbackLabel;
    const isActive = activePath === path;
    const SelectedIcon = isActive && ActiveIcon ? ActiveIcon : Icon;

    return (
      <li className="yt-nav-item" title={label}>
        <div
          className={`yt-nav-link ${isActive ? 'active' : ''}`}
          onClick={() => handleNav(path)}
        >
          <span className="yt-nav-icon" style={iconColor ? { color: iconColor } : {}}>
            {SelectedIcon && <SelectedIcon />}
          </span>
          <span className="yt-nav-label">{label}</span>
        </div>
      </li>
    );
  };

  return (
    <aside
      className={`yt-sidebar ${collapsed ? 'collapsed' : ''} ${isScrolling ? 'scrolling' : ''}`}
      onScroll={handleSidebarScroll}
    >
      {/* --- Section 1: Main Navigation with Subscriptions Dropdown --- */}
      <ul className="yt-sidebar-section m-0 p-0">
        {navItem('/', 'home', 'Home', MdOutlineHome, MdHome)}
        {navItem('/shorts', 'shorts', 'Shorts', ShortsOutlineIcon, ShortsActiveIcon)}
        
        {/* Subscriptions Item with Expandable Dropdown right here */}
        <li className="yt-nav-item yt-subs-dropdown-item" title={getTranslation('subscriptions') || 'Subscriptions'}>
          <div
            className={`yt-nav-link yt-subs-dropdown-link ${isSubscriptionsOpen ? 'open' : ''} ${activePath.startsWith('/channel/') ? 'active' : ''}`}
            onClick={() => setIsSubscriptionsOpen((prev) => !prev)}
          >
            <span className="yt-nav-icon">
              {isSubscriptionsOpen ? <MdSubscriptions /> : <MdOutlineSubscriptions />}
            </span>
            <span className="yt-nav-label">{getTranslation('subscriptions') || 'Subscriptions'}</span>
            <span className="yt-sub-expand-chevron">
              <MdChevronRight
                className={`header-chevron yt-sub-chevron ${isSubscriptionsOpen ? 'expanded' : ''}`}
              />
            </span>
          </div>

          {/* Subscribed Channels List Right Under Subscriptions with Smooth Accordion */}
          <div className={`yt-subs-dropdown-container ${isSubscriptionsOpen ? 'open' : ''}`}>
            <div className="yt-subs-dropdown-inner">
              <ul className="m-0 p-0 yt-sub-channel-list yt-subs-inline-list">
                {subscribedChannels.length > 0 ? (
                  <>
                    {(showAllSubs ? subscribedChannels : subscribedChannels.slice(0, 7)).map((ch) => {
                      const isChActive = activePath === `/channel/${ch.handle_name}` || activePath === `/channel/${ch.channel_id}`;
                      const chInitial = ch.name?.charAt(0)?.toUpperCase() || 'C';
                      const chColor = getAvatarColor(ch.name || ch.handle_name);

                      return (
                        <li key={ch.channel_id || ch.handle_name} className="yt-nav-item yt-sub-channel-item" title={ch.name}>
                          <div
                            className={`yt-nav-link yt-channel-nav-link ${isChActive ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNav(`/channel/${ch.handle_name || ch.channel_id}`);
                            }}
                          >
                            <span
                              className="yt-sidebar-channel-avatar"
                              style={{ backgroundColor: chColor }}
                            >
                              {ch.avatar_url ? (
                                <img
                                  src={normalizeMediaUrl(ch.avatar_url)}
                                  alt={ch.name}
                                  className="yt-sidebar-avatar-img"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                chInitial
                              )}
                            </span>
                            <span className="yt-nav-label yt-channel-nav-name">{ch.name}</span>
                          </div>
                        </li>
                      );
                    })}

                    {subscribedChannels.length > 7 && (
                      <li className="yt-nav-item">
                        <div
                          className="yt-nav-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllSubs(!showAllSubs);
                          }}
                        >
                          <span className="yt-nav-icon">
                            <MdKeyboardArrowDown className={`yt-chevron-animated ${showAllSubs ? 'rotated' : ''}`} />
                          </span>
                          <span className="yt-nav-label">
                            {showAllSubs ? (getTranslation('showLess') || 'Show less') : `Show ${subscribedChannels.length - 7} more`}
                          </span>
                        </div>
                      </li>
                    )}
                  </>
                ) : (
                  <li className="yt-nav-item">
                    <div className="yt-sidebar-empty-subs">
                      <span>No subscriptions yet</span>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </li>

        {navItem('/albums', 'albums', 'Albums', MdOutlineLibraryMusic, MdLibraryMusic)}
      </ul>

      <hr className="yt-sidebar-divider" />

      {/* --- Section 2: "You >" (Personalized Section) --- */}
      <div className={`yt-sidebar-section yt-personalized-section ${!isLoggedIn ? 'logged-out-blur' : ''}`}>
        <div className="yt-personalized-content">
          <div className="yt-sidebar-header" onClick={() => handleNav('/profile')}>
            <span>{getTranslation('you')}</span>
            <MdChevronRight className="header-chevron" />
          </div>
          <ul className="m-0 p-0">
            {navItem('/profile', 'yourChannel', 'Your channel', HiOutlineUser, HiUser)}
            {navItem('/history', 'history', 'History', MdOutlineHistory, RiHistoryFill)}
            {navItem('/playlists', 'playlists', 'Playlists', RiPlayList2Line, BiSolidPlaylist)}
            {navItem('/watch-later', 'watchLater', 'Watch Later', MdOutlineWatchLater, BsClockFill)}
            {navItem('/liked-videos', 'likedVideos', 'Liked videos', MdOutlineThumbUp, BiSolidLike)}
            {navItem('/your-videos', 'yourVideos', 'Your videos', MdOutlineVideoLibrary, BsCollectionPlayFill)}
            {navItem('/downloads', 'downloads', 'Downloads', MdOutlineFileDownload, BiSolidDownload)}
          </ul>
        </div>

        {/* Logged Out Overlay Banner */}
        {!isLoggedIn && (
          <div className="yt-logged-out-overlay">
            <p className="yt-logged-out-msg">
              Sign in to get your Personalized Section
            </p>
            <button className="yt-logged-out-signin-btn" onClick={onLogin}>
              <HiOutlineUserCircle size={20} />
              <span>Sign in</span>
            </button>
          </div>
        )}
      </div>

      <hr className="yt-sidebar-divider" />

      {/* --- Section 3: "More from Speedo" --- */}
      <div className="yt-sidebar-section">
        <div className="yt-sidebar-header cursor-default">
          {getTranslation('moreFromSpeedo')}
        </div>
        <ul className="m-0 p-0">
          {navItem('/prime', 'speedoPrime', 'Speedo Prime', MdWorkspacePremium, MdWorkspacePremium, '#FF0000')}
          {navItem('/tv', 'speedoTV', 'Speedo TV', MdOutlineTv, MdTv, '#FF0000')}
          {navItem('/learning', 'speedoLearning', 'Speedo Learning', MdOutlineSchool, MdSchool, '#FF0000')}
        </ul>
      </div>

      <hr className="yt-sidebar-divider" />

      {/* --- Section 4: "Explore" --- */}
      <div className="yt-sidebar-section">
        <div className="yt-sidebar-header cursor-default">
          {getTranslation('explore')}
        </div>
        <ul className="m-0 p-0">
          {navItem('/shopping', 'shopping', 'Shopping', MdOutlineShoppingBag, RiShoppingBagFill)}
          {navItem('/movies', 'movies', 'Movies & TV', MdOutlineMovie, RiFilmFill)}

          <ul className={`yt-expandable-list ${showMore ? 'open' : ''}`}>
            {navItem('/trending', 'trending', 'Trending', RiFireLine, RiFireFill)}
            {navItem('/popular', 'popular', 'Popular Channels', RiStarLine, RiStarFill)}
            {navItem('/day', 'day', 'Videos of the Day', RiCalendarEventLine, RiCalendarEventFill)}
            {navItem('/week', 'week', 'Videos of the Week', RiCalendarEventLine, RiCalendarEventFill)}
            {navItem('/month', 'month', 'Videos of the Month', RiCalendarEventLine, RiCalendarEventFill)}
            {navItem('/year', 'year', 'Videos of the Year', RiCalendarEventLine, RiCalendarEventFill)}
            {navItem('/help', 'help', 'Help', RiQuestionLine, RiQuestionFill)}
          </ul>

          <li className="yt-nav-item">
            <div
              className="yt-nav-link"
              onClick={() => setShowMore(!showMore)}
            >
              <span className="yt-nav-icon">
                <MdKeyboardArrowDown className={`yt-chevron-animated ${showMore ? 'rotated' : ''}`} />
              </span>
              <span className="yt-nav-label">
                {showMore ? getTranslation('showLess') : getTranslation('showMore')}
              </span>
            </div>
          </li>
        </ul>
      </div>

      {/* SpeedoTube INC Sidebar Footer Links & Statement */}
      {!collapsed && (
        <>
          <hr className="yt-sidebar-divider" />
          <div className="yt-sidebar-footer-section">
            <div className="yt-sidebar-footer-links">
              <button
                type="button"
                className="yt-sidebar-footer-link"
                onClick={() => window.dispatchEvent(new CustomEvent('speedotube_open_legal_modal', { detail: { tab: 'about' } }))}
              >
                About Us
              </button>
              <button
                type="button"
                className="yt-sidebar-footer-link"
                onClick={() => window.dispatchEvent(new CustomEvent('speedotube_open_legal_modal', { detail: { tab: 'privacy' } }))}
              >
                Privacy Policy
              </button>
              <button
                type="button"
                className="yt-sidebar-footer-link"
                onClick={() => window.dispatchEvent(new CustomEvent('speedotube_open_legal_modal', { detail: { tab: 'terms' } }))}
              >
                Terms & Conditions
              </button>
              <button
                type="button"
                className="yt-sidebar-footer-link"
                onClick={() => handleNav('/channel/@speedotube')}
              >
                Creators
              </button>
              <button
                type="button"
                className="yt-sidebar-footer-link"
                onClick={() => window.dispatchEvent(new CustomEvent('speedotube_open_legal_modal', { detail: { tab: 'about' } }))}
              >
                Contact Us
              </button>
            </div>
            <div className="yt-sidebar-footer-copy">
              © {new Date().getFullYear()} SpeedoTube INC.<br />All Rights Reserved.
            </div>
          </div>
        </>
      )}
    </aside>
  );
}
